<template>
  <el-popover
    placement="bottom"
    :width="360"
    trigger="click"
    popper-class="notification-popover"
    @show="handlePopoverShow"
  >
    <template #reference>
      <div class="notification-bell" :class="{ 'has-unread': unreadCount > 0 }">
        <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="bell-badge">
          <el-icon class="bell-icon" :class="{ 'shake': hasHighPriority }"><Bell /></el-icon>
        </el-badge>
      </div>
    </template>

    <div class="notification-list" v-loading="store.loading">
      <div class="notification-header">
        <span class="title">通知中心</span>
        <el-button link type="primary" size="small" @click="store.markAllAsRead" v-if="unreadCount > 0">全部已读</el-button>
      </div>

      <div v-if="notifications.length === 0" class="empty-state">
        <el-empty description="暂无新通知" :image-size="60" />
      </div>

      <div v-else class="list-content">
        <div
          v-for="item in notifications"
          :key="item.id"
          class="notification-item"
          :class="{ 'unread': !item.read }"
          @click="handleItemClick(item)"
        >
          <div class="item-icon" :class="item.type">
            <el-icon v-if="item.type === 'gift'"><Present /></el-icon>
            <el-icon v-else-if="item.type === 'payment'"><Money /></el-icon>
            <el-icon v-else-if="item.type === 'compensation'"><FirstAidKit /></el-icon>
            <el-icon v-else><Bell /></el-icon>
          </div>
          <div class="item-content">
            <div class="item-title-row">
              <span class="item-title">{{ item.title }}</span>
              <span class="item-time">{{ item.time }}</span>
            </div>
            <div class="item-desc" :class="{ 'highlight': item.type === 'gift' || item.status === 'success' }">
              {{ item.content }}
            </div>
            
            <!-- Self Repair Action -->
            <div v-if="item.type === 'payment' && item.status === 'pending'" class="item-actions">
               <el-button 
                 type="primary" 
                 size="small" 
                 :loading="repairingId === item.id"
                 @click.stop="handleSelfRepair(item)"
               >
                 {{ repairingId === item.id ? '正在连接支付网关核验...' : '刷新支付状态' }}
               </el-button>
            </div>
          </div>
          <div class="unread-dot" v-if="!item.read"></div>
        </div>
      </div>

      <div class="notification-footer">
        <el-button link @click="$router.push('/user/notifications')">查看全部</el-button>
      </div>
    </div>
  </el-popover>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Bell, Present, Money, FirstAidKit } from '@element-plus/icons-vue'
import { useNotificationStore, type NotificationItem } from '@/store/notification'
import { ElMessage } from 'element-plus'

import { useRouter } from 'vue-router'

const store = useNotificationStore()
const router = useRouter()

const notifications = computed(() => store.notifications)
const unreadCount = computed(() => store.unreadCount)
const hasHighPriority = computed(() => notifications.value.some(n => !n.read && n.displayLevel === 'HIGH'))

const repairingId = ref<string | null>(null)

const handleItemClick = (item: NotificationItem) => {
  store.markAsRead(item.id)
  router.push({ path: '/user/notifications', query: { id: item.id } })
}

let popoverTimer: any = null
const handlePopoverShow = () => {
  if (popoverTimer) clearTimeout(popoverTimer)
  popoverTimer = setTimeout(() => {
    store.fetchNotifications({ page: 1, pageSize: 10 })
    // 更新未读数，确保红点同步消除（如果需要）
    store.fetchUnreadCount()
  }, 100)
}

const handleSelfRepair = async (item: NotificationItem) => {
  if (repairingId.value) return
  
  repairingId.value = item.id
  try {
    await store.triggerSelfRepair(item.id)
    ElMessage.success('支付状态已更新，权益已到账！')
    // Play success sound (optional)
  } catch (e) {
    ElMessage.error('未查询到支付记录，请联系客服')
  } finally {
    repairingId.value = null
  }
}
</script>

<style scoped lang="scss">
.notification-bell {
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: var(--tts-bg-secondary);
  }

  .bell-icon {
    font-size: 20px;
    color: var(--tts-text-secondary);
    
    &.shake {
      animation: bell-shake 2s infinite;
      color: var(--tts-error);
    }
  }
}

@keyframes bell-shake {
  0% { transform: rotate(0); }
  10% { transform: rotate(15deg); }
  20% { transform: rotate(-15deg); }
  30% { transform: rotate(10deg); }
  40% { transform: rotate(-10deg); }
  50% { transform: rotate(0); }
  100% { transform: rotate(0); }
}

.notification-list {
  .notification-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--tts-border-light);

    .title {
      font-weight: 600;
      font-size: 16px;
      color: var(--tts-text-primary);
    }
  }

  .list-content {
    max-height: 400px;
    overflow-y: auto;
  }

  .notification-item {
    display: flex;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;
    border-bottom: 1px solid var(--tts-border-light);

    &:hover {
      background-color: var(--tts-bg-secondary);
    }

    &.unread {
      background-color: var(--el-color-primary-light-9);
    }

    .item-icon {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;

      &.gift { background-color: #e1f3d8; color: var(--tts-success); }
      &.payment { background-color: var(--el-color-primary-light-9); color: var(--tts-primary); }
      &.compensation { background-color: #fde2e2; color: var(--tts-error); }
      &.system { background-color: var(--tts-bg-tertiary); color: var(--tts-text-secondary); }
    }

    .item-content {
      flex: 1;
      min-width: 0;

      .item-title-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 4px;

        .item-title {
          font-weight: 500;
          font-size: 14px;
          color: var(--tts-text-primary);
        }

        .item-time {
          font-size: 12px;
          color: var(--tts-text-tertiary);
        }
      }

      .item-desc {
        font-size: 13px;
        color: var(--tts-text-secondary);
        line-height: 1.4;
        
        &.highlight {
          color: var(--tts-success);
          font-weight: 500;
        }
      }
      
      .item-actions {
        margin-top: 8px;
      }
    }
    
    .unread-dot {
      position: absolute;
      top: 12px;
      right: 8px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: var(--tts-error);
    }
  }

  .notification-footer {
    padding: 8px;
    text-align: center;
    border-top: 1px solid var(--tts-border-light);
  }
}
</style>
